import { jsx as _jsx } from "react/jsx-runtime";
import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
const FocusTimerContext = createContext(undefined);
export function FocusTimerProvider({ children }) {
    const { user } = useAuth();
    const [state, setState] = useState({
        isRunning: false,
        currentStep: null,
        currentBreath: null,
        timeRemaining: 0,
        totalDuration: 0,
        breathStartTime: null,
        showRitual: null
    });
    const intervalRef = useRef(null);
    const breathIntervalRef = useRef(null);
    // Store last timer state for each step (stepId -> timeRemaining)
    const stepTimerStateRef = useRef(new Map());
    // Load persisted state from localStorage on mount
    useEffect(() => {
        // Clear localStorage to always use fresh database values
        // This prevents stale timeRemaining values
        localStorage.removeItem('focusTimerState');
    }, []);
    // Persist state to localStorage
    useEffect(() => {
        if (state.currentStep) {
            localStorage.setItem('focusTimerState', JSON.stringify({
                currentStep: state.currentStep,
                currentBreath: state.currentBreath,
                timeRemaining: state.timeRemaining,
                totalDuration: state.totalDuration,
                breathStartTime: state.breathStartTime
            }));
        }
        else {
            localStorage.removeItem('focusTimerState');
        }
    }, [state]);
    // Main timer countdown
    useEffect(() => {
        if (state.isRunning && state.timeRemaining > 0) {
            intervalRef.current = setInterval(() => {
                setState(prev => {
                    const newTimeRemaining = Math.max(0, prev.timeRemaining - 1);
                    // Save timer state for current step
                    if (prev.currentStep?.id) {
                        stepTimerStateRef.current.set(prev.currentStep.id, newTimeRemaining);
                    }
                    return {
                        ...prev,
                        timeRemaining: newTimeRemaining
                    };
                });
            }, 1000);
        }
        else {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        }
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [state.isRunning, state.timeRemaining]);
    // Breath time tracking
    useEffect(() => {
        if (state.isRunning && state.currentBreath?.is_running && state.breathStartTime) {
            breathIntervalRef.current = setInterval(async () => {
                if (!state.currentBreath || !user)
                    return;
                const elapsed = Math.floor((Date.now() - state.breathStartTime) / 1000);
                const newTotalTime = (state.currentBreath.total_time_seconds || 0) + 1;
                // Update database every second
                try {
                    await supabase
                        .from('breaths')
                        .update({
                        total_time_seconds: newTotalTime,
                        updated_at: new Date().toISOString()
                    })
                        .eq('id', state.currentBreath.id);
                    // Update local state
                    setState(prev => ({
                        ...prev,
                        currentBreath: prev.currentBreath ? {
                            ...prev.currentBreath,
                            total_time_seconds: newTotalTime
                        } : null
                    }));
                }
                catch (error) {
                    console.error('Failed to update breath time:', error);
                }
            }, 1000);
        }
        else {
            if (breathIntervalRef.current) {
                clearInterval(breathIntervalRef.current);
                breathIntervalRef.current = null;
            }
        }
        return () => {
            if (breathIntervalRef.current) {
                clearInterval(breathIntervalRef.current);
            }
        };
    }, [state.isRunning, state.currentBreath?.is_running, state.breathStartTime, user]);
    const startTimer = useCallback(() => {
        setState(prev => ({ ...prev, isRunning: true }));
    }, []);
    const pauseTimer = useCallback(() => {
        setState(prev => ({ ...prev, isRunning: false }));
    }, []);
    const resetTimer = useCallback(() => {
        setState(prev => ({
            ...prev,
            isRunning: false,
            timeRemaining: prev.totalDuration
        }));
    }, []);
    const pushBackTime = useCallback(async (minutes) => {
        if (!user)
            return;
        // Capture current step before state update
        let stepId = null;
        let newDurationMinutes = 0;
        setState(prev => {
            const newTimeRemaining = Math.max(0, prev.timeRemaining + (minutes * 60));
            // Calculate new duration in minutes for database
            newDurationMinutes = Math.ceil(newTimeRemaining / 60);
            // Save to step timer state
            if (prev.currentStep?.id) {
                stepId = prev.currentStep.id;
                stepTimerStateRef.current.set(prev.currentStep.id, newTimeRemaining);
            }
            // Update local state immediately for instant UI response
            return {
                ...prev,
                timeRemaining: newTimeRemaining,
                totalDuration: newTimeRemaining,
                currentStep: prev.currentStep ? {
                    ...prev.currentStep,
                    duration: newDurationMinutes
                } : null
            };
        });
        // Update database in background (non-blocking)
        if (stepId) {
            try {
                await supabase
                    .from('steps')
                    .update({
                    duration: newDurationMinutes,
                    updated_at: new Date().toISOString()
                })
                    .eq('id', stepId);
            }
            catch (error) {
                console.error('Failed to update step duration:', error);
            }
        }
    }, [user]);
    const setCurrentStep = useCallback((step) => {
        // Save current step's timer state before switching
        setState(prev => {
            if (prev.currentStep?.id) {
                stepTimerStateRef.current.set(prev.currentStep.id, prev.timeRemaining);
            }
            return prev;
        });
        // Helper to normalize duration values (handles legacy millisecond data)
        const normalizeDuration = (durationValue) => {
            if (!durationValue)
                return 0;
            // If duration seems to be in milliseconds (> 1440 = 24 hours in minutes),
            // convert to minutes. This handles legacy data issues.
            if (durationValue > 1440) {
                return Math.round(durationValue / 60000);
            }
            return durationValue;
        };
        // Normalize duration and convert to seconds for the timer
        const normalizedMinutes = step ? normalizeDuration(step.duration) : 0;
        const durationInSeconds = normalizedMinutes * 60;
        // Check if we have saved timer state for this step
        const savedTimeRemaining = step?.id ? stepTimerStateRef.current.get(step.id) : undefined;
        const timeRemaining = savedTimeRemaining !== undefined ? savedTimeRemaining : durationInSeconds;
        console.log('[FocusTimer] Setting step:', {
            stepLabel: step?.label,
            rawDuration: step?.duration,
            normalizedMinutes,
            durationInSeconds,
            savedTimeRemaining,
            usingTimeRemaining: timeRemaining
        });
        setState(prev => ({
            ...prev,
            currentStep: step,
            currentBreath: null,
            timeRemaining,
            totalDuration: durationInSeconds,
            isRunning: false,
            breathStartTime: null
        }));
    }, []);
    const refreshBreaths = useCallback(async () => {
        if (!state.currentStep?.id)
            return;
        try {
            let query = supabase
                .from('breaths')
                .select('*')
                .eq('step_id', state.currentStep.id);
            // If this is a habit instance, filter by instance_date
            // Otherwise, filter out instances (get template or non-instance breaths)
            if (state.currentStep.instance_date) {
                query = query.eq('instance_date', state.currentStep.instance_date);
            }
            else {
                query = query.is('instance_date', null);
            }
            const { data, error } = await query.order('position', { ascending: true });
            if (error)
                throw error;
            setState(prev => ({
                ...prev,
                currentStep: prev.currentStep ? {
                    ...prev.currentStep,
                    breaths: data || []
                } : null,
                currentBreath: data?.find(b => b.id === prev.currentBreath?.id) || null
            }));
        }
        catch (error) {
            console.error('Failed to refresh breaths:', error);
        }
    }, [state.currentStep?.id, state.currentStep?.instance_date]);
    // Define pauseBreath BEFORE startBreath to avoid circular dependency
    const pauseBreath = useCallback(async () => {
        if (!user)
            return;
        // Capture current breath ID before state update
        let breathIdToUpdate = null;
        setState(prev => {
            if (!prev.currentBreath)
                return prev;
            breathIdToUpdate = prev.currentBreath.id;
            // Update state immediately for UI responsiveness
            return {
                ...prev,
                currentBreath: {
                    ...prev.currentBreath,
                    is_running: false
                },
                breathStartTime: null,
                isRunning: false
            };
        });
        // Update database after state is updated
        if (breathIdToUpdate) {
            try {
                const now = new Date().toISOString();
                const { error } = await supabase
                    .from('breaths')
                    .update({
                    is_running: false,
                    updated_at: now
                })
                    .eq('id', breathIdToUpdate);
                if (error)
                    throw error;
                // Refresh breaths after database update
                await refreshBreaths();
            }
            catch (error) {
                console.error('Failed to pause breath:', error);
            }
        }
    }, [user, refreshBreaths]);
    const startBreath = useCallback(async (breath) => {
        if (!user)
            return;
        try {
            // Pause any currently running breath first
            await pauseBreath();
            // Start the new breath
            const now = new Date().toISOString();
            const { error } = await supabase
                .from('breaths')
                .update({
                is_running: true,
                start_time: breath.start_time || now,
                updated_at: now
            })
                .eq('id', breath.id);
            if (error)
                throw error;
            // Update state with new running breath
            setState(prev => ({
                ...prev,
                currentBreath: {
                    ...breath,
                    is_running: true,
                    start_time: breath.start_time || now
                },
                breathStartTime: Date.now(),
                isRunning: true
            }));
            // Refresh all breaths to sync state
            await refreshBreaths();
        }
        catch (error) {
            console.error('Failed to start breath:', error);
        }
    }, [user, pauseBreath, refreshBreaths]);
    const completeBreath = useCallback(async () => {
        if (!state.currentBreath || !user)
            return;
        try {
            const now = new Date().toISOString();
            const { error } = await supabase
                .from('breaths')
                .update({
                completed: true,
                is_running: false,
                end_time: now,
                updated_at: now
            })
                .eq('id', state.currentBreath.id);
            if (error)
                throw error;
            setState(prev => ({
                ...prev,
                currentBreath: null,
                breathStartTime: null,
                isRunning: false
            }));
            await refreshBreaths();
        }
        catch (error) {
            console.error('Failed to complete breath:', error);
        }
    }, [state.currentBreath, user, refreshBreaths]);
    const updateBreathEstimation = useCallback(async (breathId, seconds) => {
        if (!user)
            return;
        try {
            const { error } = await supabase
                .from('breaths')
                .update({
                time_estimation_seconds: seconds,
                updated_at: new Date().toISOString()
            })
                .eq('id', breathId);
            if (error)
                throw error;
            await refreshBreaths();
        }
        catch (error) {
            console.error('Failed to update breath estimation:', error);
        }
    }, [user, refreshBreaths]);
    const setShowRitual = useCallback((ritual) => {
        setState(prev => ({ ...prev, showRitual: ritual }));
    }, []);
    const value = useMemo(() => {
        console.log('[FocusTimer] Creating context value with state:', {
            timeRemaining: state.timeRemaining,
            currentStep: state.currentStep?.label
        });
        return {
            ...state,
            startTimer,
            pauseTimer,
            resetTimer,
            pushBackTime,
            setCurrentStep,
            startBreath,
            pauseBreath,
            completeBreath,
            updateBreathEstimation,
            refreshBreaths,
            setShowRitual
        };
    }, [state, startTimer, pauseTimer, resetTimer, pushBackTime, setCurrentStep, startBreath, pauseBreath, completeBreath, updateBreathEstimation, refreshBreaths, setShowRitual]);
    return (_jsx(FocusTimerContext.Provider, { value: value, children: children }));
}
export function useFocusTimer() {
    const context = useContext(FocusTimerContext);
    if (context === undefined) {
        throw new Error('useFocusTimer must be used within a FocusTimerProvider');
    }
    return context;
}
